'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useLabsStore, usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { MODULE_COMPLETE_STEP_RE, type Step } from '@/app/(platform)/(subscription-gated)/labs/_engine/steps';
import { useInstantMotion } from '@/app/(platform)/(subscription-gated)/labs/_engine/use-instant-motion';
import { LABS_PREVIEW_EVENTS } from '@/lib/analytics/labs-preview-events';
import { usePreviewAnalytics } from './preview-analytics';
import { PREVIEW_SCREENS } from './preview-registry';
import { PREVIEW_STEPS, PREVIEW_TERMINAL_STEP, type PreviewStep } from './preview-steps';

/**
 * The public preview's flow orchestrator, the anonymous, arm-free sibling of `labs-flow.tsx`.
 *
 * It renders the SAME screens over a shorter sequence. What it deliberately does NOT carry over:
 *
 *   - **The trial bar.** Suppressed at the source by the surface (`trialBar: false`), because
 *     `useNeedsPayment` reads true for every signed-out reader and the bar would send them to
 *     `/payment` with no account.
 *   - **The arm.** No flag is read, so events fire from the first beat instead of waiting for a
 *     readiness gate that would never resolve for an anonymous visitor.
 *   - **The once-per-run claim.** That is keyed on a Clerk user id; here a run is a browser session.
 *
 * What it keeps, on purpose: the step ids, the scroll reset (QA-434, a step change is state, not a
 * navigation, so the browser keeps the previous offset and short screens land below the fold), and the
 * same 220ms enter/exit, so the two surfaces feel like one product.
 */

const isPreviewStep = (step: Step): step is PreviewStep => (PREVIEW_STEPS as readonly Step[]).includes(step);

/** Dev-only step hook, mirroring the sprint's `__labsGoTo`. Its own name, so a harness cannot drive the wrong flow. */
type PreviewDevWindow = typeof window & {
  __labsPreviewGoTo?: (step: PreviewStep) => void;
};
function PreviewRenderer() {
  const $ = _c(26);
  const step = useLabsStore(_temp);
  const archetype = useLabsStore(_temp2);
  const goTo = useLabsStore(_temp3);
  const {
    slug
  } = usePack();
  const {
    track
  } = usePreviewAnalytics();
  const instant = useInstantMotion();
  let t0;
  let t1;
  if ($[0] !== goTo) {
    t0 = () => {
      if (process.env.NODE_ENV === "production") {
        return;
      }
      const w = window as PreviewDevWindow;
      w.__labsPreviewGoTo = goTo;
      return () => {
        w.__labsPreviewGoTo = undefined;
      };
    };
    t1 = [goTo];
    $[0] = goTo;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const viewedTracked = useRef(false);
  let t2;
  let t3;
  if ($[3] !== track) {
    t2 = () => {
      if (viewedTracked.current) {
        return;
      }
      viewedTracked.current = true;
      track(LABS_PREVIEW_EVENTS.VIEWED, {
        step_count: PREVIEW_STEPS.length
      });
    };
    t3 = [track];
    $[3] = track;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Set();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const seenSteps = useRef(t4);
  let t5;
  let t6;
  if ($[7] !== archetype || $[8] !== slug || $[9] !== step || $[10] !== track) {
    t5 = () => {
      if (seenSteps.current.has(step)) {
        return;
      }
      seenSteps.current.add(step);
      track(LABS_PREVIEW_EVENTS.STEP_VIEWED, {
        step,
        step_count: PREVIEW_STEPS.length,
        step_index: (PREVIEW_STEPS as readonly Step[]).indexOf(step)
      });
      if (step === "m1-intro") {
        track(LABS_PREVIEW_EVENTS.STARTED);
      }
      const completedModule = MODULE_COMPLETE_STEP_RE.exec(step)?.[1];
      if (completedModule) {
        track(LABS_PREVIEW_EVENTS.MODULE_COMPLETED, {
          module: completedModule
        });
      }
      if (step === PREVIEW_TERMINAL_STEP) {
        if (archetype) {
          track(LABS_PREVIEW_EVENTS.ARCHETYPE_REVEALED, {
            archetype
          });
        }
        track(LABS_PREVIEW_EVENTS.COMPLETED, {
          archetype,
          vertical: slug
        });
      }
    };
    t6 = [archetype, slug, step, track];
    $[7] = archetype;
    $[8] = slug;
    $[9] = step;
    $[10] = track;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== step) {
    t7 = [step];
    $[13] = step;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(_temp4, t7);
  if (!isPreviewStep(step)) {
    goTo(PREVIEW_STEPS[0]);
    return null;
  }
  const Screen = PREVIEW_SCREENS[step];
  if (instant) {
    let t8;
    if ($[15] !== Screen) {
      t8 = <Screen />;
      $[15] = Screen;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 1,
      y: 0
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0,
      y: -8
    };
    t10 = {
      opacity: 0,
      y: 8
    };
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1]
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== Screen) {
    t12 = <Screen />;
    $[21] = Screen;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== step || $[24] !== t12) {
    t13 = <AnimatePresence initial={false} mode="wait"><motion.div animate={t8} className="flex flex-1 flex-col" exit={t9} initial={t10} key={step} transition={t11}>{t12}</motion.div></AnimatePresence>;
    $[23] = step;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
}

/**
 * The preview flow. Expects a `PackProvider`, a `LabsSurfaceProvider` and a `PreviewAnalyticsProvider`
 * above it, and a hydrated store (the route supplies all four).
 *
 * Keyed on the run counter for the same reason the sprint is: "Start over" bumps it, remounting this
 * component so the per-run analytics latches re-arm and a replay is fully re-instrumented.
 */
function _temp4() {
  window.scrollTo(0, 0);
  document.getElementById("scrollable-container")?.scrollTo(0, 0);
}
function _temp3(s_1) {
  return s_1.goTo;
}
function _temp2(s_0) {
  return s_0.archetype;
}
function _temp(s) {
  return s.step;
}
export function PreviewFlow() {
  const $ = _c(2);
  const runId = useLabsStore(_temp5);
  let t0;
  if ($[0] !== runId) {
    t0 = <PreviewRenderer key={runId} />;
    $[0] = runId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp5(s) {
  return s.runId;
}